// Signed instructions: what a trusted device tells an agent, signed with its
// instruct key. The stores (the Vault, Cloud) keep an instruction beside the
// write it belongs to and hand it to the agent as it is; the agent acts on a
// write only when its instruction checks here, so a store can neither make
// one up nor change what one says.
//
// Promise:
// - An instruction names the list version its signer knew (`list`, `n`,
//   `head`), the agent's device, the chat (null for what is the device's: its
//   settings, a login), its own id (the store keeps the write under it), its
//   kind and the SHA-256 of its content's canonical JSON (`body`). It is
//   signed (ES256, the instruct key of `by`) over the canonical JSON of
//   everything but `sig`.
// - `scope` says who may act on it: "any" (every agent of the list) or "self"
//   (only the agent of the signer's own PC: what a PC's agent tells its own
//   children). An agent takes "any" only from a member whose instruct key no
//   other process of its PC can use (`signsForAll`).
// - `checkInstruction` refuses one not for this device or chat, signed by a
//   member not in the newest list the agent knows, with a scope its signer
//   may not use, over another content, seen before, or signed more than a day
//   away from the agent's clock; one naming a newer version asks the agent to
//   catch up first (`behind`), and one naming another hash at a version the
//   agent knows is a fork (`forked`: the agent stops taking instructions).
import { z } from "zod";
import {
  canonicalJson,
  type JsonWebKey,
  sha256,
  signBytes,
  type TrustMember,
  type TrustState,
  toHex,
  verifyBytes,
} from "./trust";

export const INSTRUCTION_VERSION = 1;

export const INSTRUCTION_KINDS = [
  "message",
  "answer",
  "conditions",
  "request",
  "device_settings",
  "login",
  "create",
  "delegate",
  "notice",
] as const;
export type InstructionKind = (typeof INSTRUCTION_KINDS)[number];

export const INSTRUCTION_SCOPES = ["any", "self"] as const;
export type InstructionScope = (typeof INSTRUCTION_SCOPES)[number];

/** How far an instruction's time may be from the agent's clock. */
export const INSTRUCTION_SKEW_MS = 24 * 60 * 60 * 1000;
/** How long an agent remembers the ids it took (longer than the skew both ways). */
export const INSTRUCTION_SEEN_MS = 2 * INSTRUCTION_SKEW_MS;

const hex32 = /^[0-9a-f]{32}$/;
const hex64 = /^[0-9a-f]{64}$/;
const b64u = /^[A-Za-z0-9_-]+$/;
const Id = z.string().min(1).max(200);

export const instructionShape = z.strictObject({
  v: z.literal(INSTRUCTION_VERSION),
  list: z.string().regex(hex32),
  n: z.number().int().min(1),
  head: z.string().regex(hex64),
  device: Id,
  chat: Id.nullable(),
  id: Id,
  kind: z.enum(INSTRUCTION_KINDS),
  scope: z.enum(INSTRUCTION_SCOPES),
  body: z.string().regex(hex64),
  at: z.string().min(1).max(40),
  by: z.string().regex(hex32),
  sig: z.string().regex(b64u).length(86),
});
export type Instruction = z.infer<typeof instructionShape>;
export type UnsignedInstruction = Omit<Instruction, "sig">;

/**
 * A signed write as the stores keep it and hand it on: the instruction and
 * the content it was signed over. The agent acts on the content (after
 * checking it against `instruction.body`), never on what a store made of it.
 */
export const signedShape = z.object({
  instruction: z.unknown(),
  content: z.unknown(),
});
export interface Signed<C = unknown> {
  instruction: Instruction;
  content: C;
}

// ---- contents per kind -------------------------------------------------------

const Hash = z.string().regex(hex64);
const AgentKindShape = z.enum(["claude", "codex"]);

/** A post (the person's, or a PC's agent to its child: `from` is the parent chat). */
export const messageContent = z.strictObject({
  text: z.string().max(200_000),
  /** The SHA-256 of each attached file, in order. */
  files: z.array(Hash).max(50),
  context: z.string().max(1000).nullable(),
  from: Id.nullable().optional(),
});
export type MessageContent = z.infer<typeof messageContent>;

/** An answer to a turn item, or what the person did with one (declined a rewrite, asked for a Slack draft). */
export const answerContent = z.strictObject({
  item: Id,
  answer: z.record(z.string(), z.unknown()).nullable(),
  /** The SHA-256 of each file of the answer, in order. */
  files: z.array(Hash).max(50),
  action: z.enum(["answer", "decline", "slack_draft", "withdraw"]),
  reason: z.string().max(2000).nullable(),
});
export type AnswerContent = z.infer<typeof answerContent>;

/** The conditions a chat runs with, whole (the device it runs on among them). */
export const conditionsContent = z.strictObject({
  device: Id,
  agentKind: AgentKindShape,
  model: z.string().max(200).nullable(),
  effort: z.string().max(100).nullable(),
  cwd: z.string().max(1000).nullable(),
  otherMcp: z.enum(["none", "read", "all"]),
});
export type ConditionsContent = z.infer<typeof conditionsContent>;

/** A request about a chat's run, as the person asked it on a screen. */
export const requestContent = z.strictObject({
  kind: z.enum([
    "cancel",
    "read_now",
    "withdraw",
    "compact",
    "rewind",
    "rewind_preview",
  ]),
  /** The post a request is about (read now, withdraw, rewind to). */
  entryId: Id.nullable(),
  /** Rewind: the conversation goes back too. */
  conversation: z.boolean().nullable(),
  /** Rewind: the files go back too. */
  files: z.boolean().nullable(),
});
export type RequestContent = z.infer<typeof requestContent>;

/** Whether a request the store hands is the one the person signed. */
export function requestMatches(
  c: RequestContent,
  req: {
    kind: string;
    entryId?: string | null;
    conversation?: boolean;
    files?: unknown;
  },
): boolean {
  if (c.kind !== req.kind) return false;
  switch (req.kind) {
    case "read_now":
    case "withdraw":
      return c.entryId === (req.entryId ?? null);
    case "rewind":
      return (
        (c.conversation ?? false) === (req.conversation ?? false) &&
        (c.files ?? false) === (req.files != null)
      );
    case "rewind_preview":
      return c.files === true;
    default:
      return true;
  }
}

/**
 * Delegating a Case's Actions (one instruction for every chat the delegation
 * makes): the Case, the chat that delegated (null: the person on a screen),
 * whether that chat ran without a folder (its children do too), and the
 * conditions the chats run with (null: the delegating chat's).
 */
export const delegateContent = z.strictObject({
  case: Id,
  from: Id.nullable(),
  restricted: z.boolean(),
  conditions: conditionsContent.nullable(),
});
export type DelegateContent = z.infer<typeof delegateContent>;

/** What happened in a child chat, as its PC's agent tells the parent. */
export const noticeContent = z.strictObject({
  child: Id,
  kind: z.string().min(1).max(50),
  item: Id.nullable(),
});
export type NoticeContent = z.infer<typeof noticeContent>;

/** What a signer is asked to sign: the rest it fills in itself. */
export interface InstructionDraft {
  device: string;
  chat: string | null;
  kind: InstructionKind;
  /** The content (hashed into `body`); its form per kind is in `instructionContent`. */
  content: unknown;
  /** Chosen by the caller when it must know it before (the write's id); else made. */
  id?: string;
}

const utf8 = (text: string) => new TextEncoder().encode(text);

/** `body` of a content: the hex SHA-256 of its canonical JSON. */
export async function contentHash(content: unknown): Promise<string> {
  return toHex(await sha256(utf8(canonicalJson(content))));
}

/** The bytes signed: the canonical JSON of everything but `sig`. */
export function instructionBytes(i: UnsignedInstruction): Uint8Array {
  const { sig: _sig, ...rest } = i as Instruction;
  return utf8(canonicalJson(rest));
}

/** The signer's side: a draft on top of the list version it knows. */
export async function signInstruction(
  draft: InstructionDraft,
  signer: {
    state: Pick<TrustState, "list" | "n" | "hash">;
    memberId: string;
    scope: InstructionScope;
    /** Signs with the instruct key (a key in a keychain or an enclave signs where it is). */
    sign: (bytes: Uint8Array) => Promise<string>;
    now?: () => Date;
    newId?: () => string;
  },
): Promise<Instruction> {
  const unsigned: UnsignedInstruction = {
    v: INSTRUCTION_VERSION,
    list: signer.state.list,
    n: signer.state.n,
    head: signer.state.hash,
    device: draft.device,
    chat: draft.chat,
    id: draft.id ?? signer.newId?.() ?? globalThis.crypto.randomUUID(),
    kind: draft.kind,
    scope: signer.scope,
    body: await contentHash(draft.content),
    at: (signer.now?.() ?? new Date()).toISOString(),
    by: signer.memberId,
  };
  return { ...unsigned, sig: await signer.sign(instructionBytes(unsigned)) };
}

/** Signs with a private key held as JWK (a PC's file, tests). */
export const jwkSigner =
  (privateKey: JsonWebKey) =>
  (bytes: Uint8Array): Promise<string> =>
    signBytes(privateKey, bytes);

/**
 * Whether a member's "any" instructions are taken by every agent: its instruct
 * key is kept where no other process of its device can use it (a phone's
 * enclave, a mac's keychain: `instructs: "any"`). A PC whose key another
 * process of its own can read (a file, Linux's and Windows' stores) signs
 * for itself only.
 */
export const signsForAll = (member: TrustMember): boolean =>
  member.instructs !== undefined
    ? member.instructs === "any"
    : member.kind === "phone";

export type InstructionProblem =
  | "shape"
  | "device"
  | "chat"
  | "kind"
  | "other_list"
  | "behind"
  | "forked"
  | "not_member"
  | "scope"
  | "signature"
  | "body"
  | "replayed"
  | "clock";

export type InstructionCheck =
  | { ok: true; instruction: Instruction; member: TrustMember }
  | { ok: false; problem: InstructionProblem };

/** What the agent knows to check with. */
export interface InstructionBook {
  state: TrustState;
  /** The hashes of the list's versions it knows (index n - 1). */
  hashes: readonly string[];
  /** This agent's device and the member of its own PC (null: a PC not in the list, e.g. a cloud device). */
  device: string;
  self: string | null;
  seen(id: string): boolean;
  now(): number;
}

/**
 * Checks an instruction against what the agent knows: `expect` is the device's
 * part of what the store says it belongs to (its chat, the kinds that fit the
 * write) and `content` the content the store handed with it.
 */
export async function checkInstruction(
  raw: unknown,
  expect: {
    chat: string | null;
    kinds: readonly InstructionKind[];
    content: unknown;
  },
  book: InstructionBook,
): Promise<InstructionCheck> {
  const parsed = instructionShape.safeParse(raw);
  if (!parsed.success) return { ok: false, problem: "shape" };
  const i = parsed.data;
  if (i.device !== book.device) return { ok: false, problem: "device" };
  if (i.chat !== expect.chat) return { ok: false, problem: "chat" };
  if (!expect.kinds.includes(i.kind)) return { ok: false, problem: "kind" };
  const { state } = book;
  if (i.list !== state.list) return { ok: false, problem: "other_list" };
  if (i.n > state.n) return { ok: false, problem: "behind" };
  if (book.hashes[i.n - 1] !== i.head) return { ok: false, problem: "forked" };
  const member = state.members.find((m) => m.id === i.by);
  if (!member) return { ok: false, problem: "not_member" };
  const mayAll = signsForAll(member);
  if (
    i.scope === "self"
      ? member.id !== book.self
      : !mayAll && member.id !== book.self
  )
    return { ok: false, problem: "scope" };
  if (!(await verifyBytes(member.instructKey, instructionBytes(i), i.sig)))
    return { ok: false, problem: "signature" };
  if ((await contentHash(expect.content)) !== i.body)
    return { ok: false, problem: "body" };
  if (book.seen(i.id)) return { ok: false, problem: "replayed" };
  const at = Date.parse(i.at);
  if (!Number.isFinite(at) || Math.abs(at - book.now()) > INSTRUCTION_SKEW_MS)
    return { ok: false, problem: "clock" };
  return { ok: true, instruction: i, member };
}
